/**
 * 施術録検索 list page (/treatment_records/init — 施術録出力).
 * Clinic-wide search for rezepts to select + open 施術録 print modal.
 * Works for both A2 and R2 via X-Service-Type header (from useTheme).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { AppButton } from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import useAppToast from '@/hooks/useAppToast';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { monthValueToIso } from '@/utils/dateConverter';

import { useTreatmentRecordSearch } from '@/hooks/main/useTreatmentRecordList';
import TreatmentRecordPrintModal from '../components/TreatmentRecordPrintModal';
import TreatmentRecordSearchForm, {
  type TreatmentRecordSearchFormValues,
} from '../components/TreatmentRecordSearchForm';
import TreatmentRecordTable from '../components/TreatmentRecordTable';
import {
  LIST_PAGE_SIZE,
  NO_ROWS_MESSAGE,
  NO_SELECTION_MESSAGE,
  SEARCH_RADIO_NAME,
  SEARCH_RADIO_SERIAL,
  SORT_BY_SERIAL,
  YEARMONTH_REQUIRED_MESSAGE,
} from '../constants';
import type { TreatmentRecordSearchPayload } from '../types';

const DEFAULT_FORM: TreatmentRecordSearchFormValues = {
  searchYearmonth: '',
  treatmentYearmonth: '',
  searchRadio: '',
  serialNumber: '',
  patientName: '',
  sortRadio: SORT_BY_SERIAL,
};

/**
 * Compute default 提出年月 (R2 parity):
 *   - current day < 16 → current month
 *   - current day >= 16 → next month
 * Returns ISO 'YYYY-MM'.
 */
function computeDefaultSearchYearmonth(): string {
  const now = dayjs();
  const target = now.date() < 16 ? now : now.add(1, 'month');
  return target.format('YYYY-MM');
}

function buildPayload(
  form: TreatmentRecordSearchFormValues,
  page: number
): TreatmentRecordSearchPayload {
  return {
    search_yearmonth: monthValueToIso(form.searchYearmonth) ?? undefined,
    treatment_yearmonth: monthValueToIso(form.treatmentYearmonth) ?? undefined,
    search_radio: form.searchRadio || undefined,
    serial_number:
      form.searchRadio === SEARCH_RADIO_SERIAL ? form.serialNumber || undefined : undefined,
    patient_name:
      form.searchRadio === SEARCH_RADIO_NAME ? form.patientName || undefined : undefined,
    sort_radio: form.sortRadio,
    page,
    per_page: LIST_PAGE_SIZE,
  };
}

const TreatmentRecordListPage: React.FC = () => {
  const toast = useAppToast();
  const sidebarOpen = useSidebarOpen();
  const { serviceType } = useTheme();
  // Sync filter state → URL query so AppTable's built-in filtered-empty state
  // message ('該当する検索結果がありません。') kicks in when a search yields 0 rows.
  const [, setSearchParams] = useSearchParams();

  const initialValues = useMemo<TreatmentRecordSearchFormValues>(
    () => ({
      ...DEFAULT_FORM,
      searchYearmonth: computeDefaultSearchYearmonth(),
    }),
    []
  );

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Spec (GUI_A.7_1 / GUI_B.7_1): on first load the table shows the default
  // empty state ("表示するデータがありません"). Search runs only when the user
  // clicks 検索 — so activePayload starts as null.
  const [activePayload, setActivePayload] = useState<TreatmentRecordSearchPayload | null>(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const { data: searchResult, isLoading } = useTreatmentRecordSearch(activePayload);

  const rows = searchResult?.data ?? [];
  const pagination = searchResult?.pagination ?? null;
  const totalCount = pagination?.total ?? 0;

  // Service-type switch (A2 ↔ R2): reset filters + clear results so the user
  // sees the spec'd default empty state again. Skipped on first mount.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    setSelectedIds(new Set());
    setActivePayload(null);
  }, [serviceType, initialValues]);

  // Mirror filter state in the URL so AppTable's hasFilters detection works
  // (the table uses useSearchParams() to decide the empty-state message).
  // Skipped before the first search so initial URL stays clean.
  useEffect(() => {
    if (!activePayload) return;
    const next: Record<string, string> = {};
    if (activePayload.search_yearmonth) next.search_yearmonth = activePayload.search_yearmonth;
    if (activePayload.treatment_yearmonth)
      next.treatment_yearmonth = activePayload.treatment_yearmonth;
    if (activePayload.search_radio) next.search_radio = activePayload.search_radio;
    if (activePayload.serial_number) next.serial_number = activePayload.serial_number;
    if (activePayload.patient_name) next.patient_name = activePayload.patient_name;
    next.sort_radio = activePayload.sort_radio;
    next.page = String(activePayload.page);
    setSearchParams(next, { replace: true });
  }, [activePayload, setSearchParams]);

  const handleSearch = useCallback((form: TreatmentRecordSearchFormValues) => {
    setSelectedIds(new Set());
    setActivePayload(buildPayload(form, 1));
  }, []);

  const handleEmptyYearmonth = useCallback(() => {
    // bypassSuppression: validation feedback must always render, even if a
    // recent network error has temporarily suppressed global toasts.
    toast.error(YEARMONTH_REQUIRED_MESSAGE, { bypassSuppression: true });
  }, [toast]);

  const handleOpenPrintModal = useCallback(() => {
    if (rows.length === 0) {
      toast.error(NO_ROWS_MESSAGE, { bypassSuppression: true });
      return;
    }
    if (selectedIds.size === 0) {
      toast.error(NO_SELECTION_MESSAGE, { bypassSuppression: true });
      return;
    }
    setIsPrintModalOpen(true);
  }, [rows.length, selectedIds.size, toast]);

  const selectedRezeptIds = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const totalCountLabel = `該当件数: ${pagination && pagination.total > 0 ? pagination?.total : rows.length} 件`;
  return (
    <>
      <div className="pb-24 space-y-4">
        <CollapsibleCard title="施術録検索">
          <TreatmentRecordSearchForm
            key={serviceType}
            initialValues={initialValues}
            onSearch={handleSearch}
            onEmptyYearmonthError={handleEmptyYearmonth}
            isLoading={isLoading}
            totalCount={totalCount}
          />
        </CollapsibleCard>

        <CollapsibleCard>
          <div className="mb-2 text-sm text-gray-700">{totalCountLabel}</div>
          <TreatmentRecordTable
            data={rows}
            selectedIds={selectedIds}
            onSelectChange={setSelectedIds}
            isLoading={isLoading}
          />
        </CollapsibleCard>
      </div>

      <BottomBar
        variant="action"
        visible
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        right={
          <AppButton type="primary" className="rounded" onClick={handleOpenPrintModal}>
            施術録設定
          </AppButton>
        }
      />

      <TreatmentRecordPrintModal
        isOpen={isPrintModalOpen}
        rezeptIds={selectedRezeptIds}
        onClose={() => setIsPrintModalOpen(false)}
      />
    </>
  );
};

export default TreatmentRecordListPage;
